import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";

import OTPInputField from "../common/OTPInputField";
import Loader from "../common/Loader";

import {
  sendOtpRequest,
  verifyOtpRequest,
} from "src/redux/slices/onboardingSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { STORAGE_KEYS } from "src/constants/common";
import { useNavigate } from "react-router-dom";
import storage from "src/helpers/storage";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import { toast } from "sonner";
import supportPrograms from "src/data/app/programs";

interface OTPProps {
  title: string;
  description: string;
  mobileNumber: string;
  onBack: () => void;
  onNext: (otp: string) => void;
  medical_condition_group_id?: string;
  support_program_type?: string;
  support_program_key?: string;
}

const OTP: React.FC<OTPProps> = ({
  title,
  description,
  mobileNumber,
  onBack,
  onNext,
  medical_condition_group_id,
  support_program_type,
  support_program_key,
}) => {
  const dispatch = useAppDispatch();
  const { sendOtp, verifyOtp } = useAppSelector((state) => state.onboarding);

  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(false);
  const navigate = useNavigate();

  const [resendTimer, setResendTimer] = useState(30);

  const handleOtpChange = async (enteredOtp: string) => {
    setOtp(enteredOtp);
    setOtpError(false);

    if (enteredOtp.length === 6) {
      await handleVerifyOtp(enteredOtp);
    }
  };

  const handleResendOtp = async () => {
    // Event: RESEND_OTP_CLICKED
    analytics.trackEvent(EVENT_NAMES.RESEND_OTP_CLICKED, {
      mobile_number: mobileNumber,
      screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
      support_program_key: support_program_key,
    });

    setOtpError(false);
    setOtp("");

    const payload = {
      payload: {
        contactNo: mobileNumber,
        otpType: "LOGIN",
      },
      headers: {
        userFrom: "PSP",
        source: "GoodFlipCare",
      },
    };

    new Promise((resolve, reject) => {
      dispatch(sendOtpRequest({ payload, resolve, reject }));
    });
  };

  const handleVerifyOtp = async (enteredOtp: string) => {
    // Event: VERIFY_OTP_CLICKED
    analytics.trackEvent(EVENT_NAMES.VERIFY_OTP_CLICKED, {
      mobile_number: mobileNumber,
      otp: enteredOtp,
      screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
      support_program_key: support_program_key,
    });

    const payload: any = {
      payload: {
        contactNo: mobileNumber,
        otpType: "LOGIN",
        otp: Number(enteredOtp),
      },
      headers: {
        userFrom: "PSP",
        source: "GoodFlipCare",
      },
    };

    payload.payload.medical_condition_group_id = medical_condition_group_id;
    payload.payload.patient_meta_info = {
      support_program_type: support_program_type,
      support_program_key: support_program_key,
    };

    new Promise((resolve, reject) => {
      dispatch(verifyOtpRequest({ payload, resolve, reject }));
    });
  };

  useEffect(() => {
    analytics.pageView(SCREEN_NAMES.OTP_CAPTURE_SCREEN);
  }, []);

  useEffect(() => {
    if (Number(sendOtp?.data?.code) === 1) {
      // Event: RE-SEND_OTP_SUCCESS
      analytics.trackEvent(EVENT_NAMES.RESEND_OTP_SUCCESS, {
        mobile_number: mobileNumber,
        screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
        support_program_key: support_program_key,
      });
      setResendTimer(30);
      toast.success("OTP Re-sent successfully!");
    } else if (Number(sendOtp?.data?.code) === 0) {
      // Event: RE-SEND_OTP_ERROR
      analytics.trackEvent(EVENT_NAMES.RESEND_OTP_ERROR, {
        mobile_number: mobileNumber,
        screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
        support_program_key: support_program_key,
      });
      toast.error(sendOtp?.data?.message || "Something went wrong!");
    }
  }, [sendOtp]);

  useEffect(() => {
    if (verifyOtp?.data) {
      // Event: VERIFY_OTP_SUCCESS
      analytics.trackEvent(EVENT_NAMES.VERIFY_OTP_SUCCESS, {
        mobile_number: mobileNumber,
        otp: otp,
        screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
        support_program_key: support_program_key,
      });
      if (verifyOtp?.data?.newUser) {
        // Event: NEW_USER_FOUND
        analytics.trackEvent(EVENT_NAMES.FOUND_NEW_USER, {
          mobile_number: mobileNumber,
          otp,
          screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
          support_program_key: support_program_key,
        });
        storage.setItem(STORAGE_KEYS.AUTH_TOKEN, verifyOtp?.data?.token);
        onNext(otp);
      } else {
        analytics.trackEvent(EVENT_NAMES.FOUND_EXISTING_USER, {
          mobile_number: mobileNumber,
          otp,
          screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
          support_program_key: support_program_key,
        });

        const userPsp = verifyOtp?.data?.patient_meta_info?.support_program_key;

        // Navigate to existing user screen with the app name to download the app
        const appName = supportPrograms[userPsp as keyof typeof supportPrograms]
          ? "GOODFLIP_CARE"
          : "GOODFLIP";
        const supportProgramKey =
          supportPrograms[userPsp as keyof typeof supportPrograms]
            ?.support_program_key;
        navigate(
          `/programs/${supportProgramKey}/onboarding/existing-user?appName=${appName}`
        );
      }
    } else if (verifyOtp?.error) {
      // Event: VERIFY_OTP_ERROR
      analytics.trackEvent(EVENT_NAMES.VERIFY_OTP_ERROR, {
        mobile_number: mobileNumber,
        otp: otp,
        screen_name: SCREEN_NAMES.OTP_CAPTURE_SCREEN,
        support_program_key: support_program_key,
        error: verifyOtp?.error || "Error while verifying OTP",
      });
      setOtpError(true);
    }
  }, [verifyOtp]);

  useEffect(() => {
    setResendTimer(30);
    const timer = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="p-4 flex flex-col gap-6">
      <div>
        <button
          className="flex items-center gap-1 px-4 py-2 -ml-4"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 text-gray-500" />
          <p className="text-sm text-gray-500">Back</p>
        </button>

        <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          {title}
        </h1>

        <p className="text-[var(--text-secondary)]">
          {description} +91 {mobileNumber}
        </p>
      </div>

      <OTPInputField
        label="Secure Code"
        value={otp}
        onChange={handleOtpChange}
        // className="mt-6"
        error={otpError}
        helperText={otpError ? "Invalid OTP. Please try again." : ""}
        // disabled={loading}
      />

      <div className="flex items-center gap-2">
        Didn't receive OTP?{" "}
        {resendTimer > 0 ? (
          <p className="text-sm text-gray-500">Resend code in {resendTimer}</p>
        ) : (
          <p className="text-sm text-gray-500">
            <button
              className="text-[var(--app-primary)]"
              onClick={handleResendOtp}
            >
              Resend
            </button>
          </p>
        )}
      </div>

      {verifyOtp?.isLoading && (
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default OTP;
